import React, { useMemo } from 'react';
import Dropdown from '#app/interface/cabinet/header/Dropdown/Dropdown';
import type { OverflowActionsMenuProps } from './types';
import { useOverflowVisibleCount } from './useOverflowVisibleCount';
import {
  Container,
  VisibleContainer,
  VisibleItem,
  MoreButtonContainer,
  OffScreenContainer,
  OffScreenItem,
  HiddenContainer,
  HiddenItem,
} from './styles';

/** Отступ попапа от кнопки «Ещё» (px) — меню ниже кнопки, но без большой «мёртвой зоны» (задержка закрытия даёт успеть перевести курсор) */
const DROPDOWN_TOP_OFFSET = 22;
/** Задержка закрытия (ms) — даёт время перевести курсор с кнопки в меню */
const DROPDOWN_CLOSE_DELAY = 200;

/**
 * OverflowActionsMenu — автоматическое скрытие элементов, не помещающихся в одну строку,
 * в dropdown «Ещё» (overflow menu).
 */
const OverflowActionsMenu: React.FC<OverflowActionsMenuProps> = ({
  items,
  gap = 6,
  availableWidth = 0,
  minVisibleItems = 0,
  moreButton,
  onMeasure,
}) => {
  const {
    hiddenContainerRef,
    moreButtonRef,
    visibleCount,
    isCalculating,
  } = useOverflowVisibleCount({
    itemsCount: items.length,
    availableWidth,
    gap,
    minVisibleItems,
    onMeasure,
  });

  const visibleItems = items.slice(0, visibleCount);
  const hiddenItems = items.slice(visibleCount);

  const dropdownLinks = useMemo(
    () =>
      hiddenItems.map((item, index) => ({
        id: item.id || `overflow-menu-item-${visibleCount + index}`,
        text: item.text,
        onClick: item.onClick,
        link: item.link,
      })),
    [hiddenItems, visibleCount]
  );

  //@ts-ignore
  const DropdownComponent = Dropdown as React.ComponentType<{
    id: string;
    width: string;
    disableXPositioning: boolean;
    containerId: string;
    links: typeof dropdownLinks;
    style: React.CSSProperties;
    topOffset: number;
    closeDelay: number;
    children: React.ReactNode;
  }>;

  return (
    <Container>
      <VisibleContainer $isCalculating={isCalculating}>
        {visibleItems.map((item, index) => (
          <VisibleItem
            key={item.id || `visible-${index}`}
            $gap={gap}
            $index={index}
          >
            {item.component}
          </VisibleItem>
        ))}
        <MoreButtonContainer
          ref={moreButtonRef}
          $gap={gap}
          $hasVisibleItems={visibleItems.length > 0}
          $hasHiddenItems={hiddenItems.length > 0}
        >
          <DropdownComponent
            id="overflow-menu-dropdown"
            width="auto"
            disableXPositioning={true}
            containerId="overflow-menu-dropdown-container"
            links={dropdownLinks}
            style={{ marginRight: 0, marginLeft: 0 }}
            topOffset={DROPDOWN_TOP_OFFSET}
            closeDelay={DROPDOWN_CLOSE_DELAY}
          >
            {moreButton}
          </DropdownComponent>
        </MoreButtonContainer>
      </VisibleContainer>

      <OffScreenContainer>
        {hiddenItems.map((item, index) => (
          <OffScreenItem key={item.id || `hidden-visible-${index}`}>
            {item.component}
          </OffScreenItem>
        ))}
      </OffScreenContainer>

      <HiddenContainer ref={hiddenContainerRef} $gap={gap}>
        {items.map((item, index) => (
          <HiddenItem key={item.id || `hidden-${index}`}>
            {item.component}
          </HiddenItem>
        ))}
      </HiddenContainer>
    </Container>
  );
};

export default OverflowActionsMenu;
